/**
 * Account gate: onboarding vs. dashboard routing (APP-002 §30).
 *
 * Sits between AccountBootstrap (backend account ready) and the layout. It
 * implements the route-guard matrix from APP-002 §30 for the signed-in +
 * account-ready case AND owns the shell the gated content renders in:
 *
 *   signed in + account ready + onboarding incomplete + not on /onboarding
 *       → redirect to /onboarding
 *   signed in + onboarding complete + on /onboarding
 *       → redirect to /dashboard
 *
 * While onboarding is incomplete the content renders in the FOCUS shell
 * (DashboardLayout variant="focus"): primary navigation is hidden, so no
 * sidebar/nav link can navigate out of the onboarding trap and bounce the
 * user back into a remounted (step-reset) onboarding. In-flow destination
 * links complete onboarding before navigating (OnboardingRoute), and the
 * current step is persisted in sessionStorage so any remount (redirect
 * bounce, external-navigation back, refresh) resumes where the user left off
 * instead of resetting to step 1.
 *
 * AuthBoundary (signed-out) and AccountBootstrap (sync-pending / disabled /
 * unavailable) handle the other rows of the matrix. This gate only reads the
 * LOCAL completion marker after the backend account resolved `ready`, so the
 * marker is never authoritative for account readiness (APP-002 §6).
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { DashboardIdentityResponse } from '@/api/dashboard-types';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { useOnboardingCompletion } from '@/features/onboarding/useOnboardingCompletion';

export interface AccountGateProps {
  identity: DashboardIdentityResponse;
  children: ReactNode;
}

export function AccountGate({ identity, children }: AccountGateProps) {
  const completion = useOnboardingCompletion();
  const location = useLocation();
  const onOnboarding = location.pathname === '/onboarding';

  // Completed onboarding + visiting /onboarding → send to dashboard.
  if (completion.completed && onOnboarding) {
    return <Navigate to="/dashboard" replace />;
  }

  // Incomplete onboarding + NOT on /onboarding → send to onboarding, but only
  // when the user isn't already on an auth/onboarding route. The return path
  // is preserved so the user lands where they intended after completing.
  if (!completion.completed && !onOnboarding) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/onboarding?redirect=${redirect}`} replace />;
  }

  // The shell choice is the gate's: the onboarding trap renders without
  // primary navigation (focus); completed users get the full app shell. The
  // resolved identity anchors the layout's identity context.
  return (
    <DashboardLayout identity={identity} variant={completion.completed ? 'app' : 'focus'}>
      {children}
    </DashboardLayout>
  );
}
